import React, { useEffect, useState } from 'react';
import Rodal from '../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader, Row } from 'reactstrap';
import './NdaTermsPopup.scss';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { NdaTermSelection } from '../../../slices/marketplace/timeline/models/nda-term-selection';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { DataNdaTermsType } from '../../../slices/marketplace/timeline/models/enums/data-nda-term-type';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { NdaTerms } from '../../../slices/marketplace/timeline/models/nda-term-model';

type NonSolicitationChoice = {
    visibilityType: 'Visible' | 'Blinded';
    duration: DataNdaTermsType;
};

interface FinalNdaTermsPopupProps {
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    fetchData(): void;
    onSubmit(): void;
    ndaTermSelection: NdaTermSelection;
    engagementId: number;
}

export const FinalNdaTermsPopup = (props: FinalNdaTermsPopupProps) => {
    const axios = useStateSelector((s) => s.core.axios);
    const buyer = useStateSelector((s) => s.buyersState.targetBuyer);
    const [isLoading, setIsLoading] = useState(false);
    const [nonDisclosurePeriod, setNonDisclosurePeriod] =
        useState<DataNdaTermsType | null>(null);
    const [dvmChoice, setDvmChoice] = useState<NonSolicitationChoice | null>(
        null
    );
    const [staffChoice, setStaffChoice] =
        useState<NonSolicitationChoice | null>(null);

    const [dvmOptions, setDvmOptions] = useState<
        Array<{ value: string; label: string }>
    >([]);
    const [staffOptions, setStaffOptions] = useState<
        Array<{ value: string; label: string }>
    >([]);

    const getBuyerTerm = () => {
        const buyerTerm = props.ndaTermSelection.ndaTermsList
            .filter(
                (x) =>
                    x.party === TimelineParty.Buyer &&
                    x.buyerId === buyer?.id &&
                    !!x.agreedTermsToDocumentList?.length
            )
            .sort(
                (a, b) =>
                    new Date(b.dateCreated).getTime() -
                    new Date(a.dateCreated).getTime()
            )[0];
        return buyerTerm;
    };

    const isInAgreement = (buyerTerm: NdaTerms, sellerTerm: NdaTerms) => {
        if (buyerTerm.nonDisclosurePeriod < sellerTerm.nonDisclosurePeriod)
            return false;

        const isDvmNamesVisibleProvided =
            buyerTerm.nonSolicitationDvmNamesVisible !== null &&
            buyerTerm.nonSolicitationDvmNamesVisible !== undefined &&
            sellerTerm.nonSolicitationDvmNamesVisible !== null &&
            sellerTerm.nonSolicitationDvmNamesVisible !== undefined;

        var dvmVisibleMatch =
            isDvmNamesVisibleProvided &&
            buyerTerm.nonSolicitationDvmNamesVisible >=
                sellerTerm.nonSolicitationDvmNamesVisible;

        const isDvmNamesBlindedProvided =
            buyerTerm.nonSolicitationDvmNamesBlinded !== null &&
            buyerTerm.nonSolicitationDvmNamesBlinded !== undefined &&
            sellerTerm.nonSolicitationDvmNamesBlinded !== null &&
            sellerTerm.nonSolicitationDvmNamesBlinded !== undefined;

        var dvmBlindedMatch =
            isDvmNamesBlindedProvided &&
            buyerTerm.nonSolicitationDvmNamesBlinded >=
                sellerTerm.nonSolicitationDvmNamesBlinded;

        if (!dvmVisibleMatch && !dvmBlindedMatch) return false;

        const isStaffNameVisibleProvided =
            buyerTerm.nonSolicitationStaffNamesVisible !== null &&
            buyerTerm.nonSolicitationStaffNamesVisible !== undefined &&
            sellerTerm.nonSolicitationStaffNamesVisible !== null &&
            sellerTerm.nonSolicitationStaffNamesVisible !== undefined;

        var staffVisibleMatch =
            isStaffNameVisibleProvided &&
            buyerTerm.nonSolicitationStaffNamesVisible >=
                sellerTerm.nonSolicitationStaffNamesVisible;

        const isStaffNameBlindedProvided =
            buyerTerm.nonSolicitationStaffNamesBlinded !== null &&
            buyerTerm.nonSolicitationStaffNamesBlinded !== undefined &&
            sellerTerm.nonSolicitationStaffNamesBlinded !== null &&
            sellerTerm.nonSolicitationStaffNamesBlinded !== undefined;

        var staffBlindedMatch =
            isStaffNameBlindedProvided &&
            buyerTerm.nonSolicitationStaffNamesBlinded >=
                sellerTerm.nonSolicitationStaffNamesBlinded;
        if (!staffVisibleMatch && !staffBlindedMatch) return false;

        return true;
    };

    useEffect(() => {
        initializeOptions();
    }, [props.ndaTermSelection]);

    const initializeOptions = () => {
        const buyerTerm = getBuyerTerm();
        if (!buyerTerm) return;

        const maxDateLockedItem = buyerTerm.agreedTermsToDocumentList.reduce(
            (maxItem, currentItem) => {
                if (!maxItem) return currentItem;

                const maxDate = new Date(maxItem.dateLocked);
                const currentDate = new Date(currentItem.dateLocked);

                return currentDate > maxDate ? currentItem : maxItem;
            },
            null
        );

        const sellerTerm = props.ndaTermSelection.ndaTermsList.find(
            (x) => x.id === maxDateLockedItem?.agreedSellerNdaTermsId
        );

        if (!sellerTerm) return;

        const isBuyerInAgreement = isInAgreement(buyerTerm, sellerTerm);
        initializeNonDisclosurePeriodOptions(sellerTerm);
        initializeNonSolicitationDvmNamesVisibilityOptions(
            buyerTerm,
            sellerTerm,
            isBuyerInAgreement
        );
        initializeNonSolicitationStaffNamesVisibilityOptions(
            buyerTerm,
            sellerTerm,
            isBuyerInAgreement
        );
    };

    const initializeNonDisclosurePeriodOptions = (sellerTerm: NdaTerms) => {
        setNonDisclosurePeriod(sellerTerm.nonDisclosurePeriod);
    };

    const initializeNonSolicitationStaffNamesVisibilityOptions = (
        buyerTerm: NdaTerms,
        sellerTerm: NdaTerms,
        isBuyerInAgreement: boolean
    ) => {
        const nextStaffOptions: Array<{ value: string; label: string }> = [];
        if (
            sellerTerm.nonSolicitationStaffNamesVisible != null &&
            buyerTerm.nonSolicitationStaffNamesVisible != null &&
            (!isBuyerInAgreement ||
                sellerTerm.nonSolicitationStaffNamesVisible <=
                    buyerTerm.nonSolicitationStaffNamesVisible)
        ) {
            nextStaffOptions.push({
                value: JSON.stringify({
                    visibilityType: 'Visible',
                    duration: sellerTerm.nonSolicitationStaffNamesVisible,
                }),
                label: `Visible ${formatYears(
                    sellerTerm.nonSolicitationStaffNamesVisible
                )}`,
            });
        }
        if (
            sellerTerm.nonSolicitationStaffNamesBlinded != null &&
            buyerTerm.nonSolicitationStaffNamesBlinded != null &&
            (!isBuyerInAgreement ||
                sellerTerm.nonSolicitationStaffNamesBlinded <=
                    buyerTerm.nonSolicitationStaffNamesBlinded)
        ) {
            nextStaffOptions.push({
                value: JSON.stringify({
                    visibilityType: 'Blinded',
                    duration: sellerTerm.nonSolicitationStaffNamesBlinded,
                }),
                label: `Blinded ${formatYears(
                    sellerTerm.nonSolicitationStaffNamesBlinded
                )}`,
            });
        }
        setStaffOptions(nextStaffOptions);
        if (nextStaffOptions.length === 1) {
            const single = JSON.parse(
                nextStaffOptions[0].value
            ) as NonSolicitationChoice;
            setStaffChoice(single);
        }
    };

    const initializeNonSolicitationDvmNamesVisibilityOptions = (
        buyerTerm: NdaTerms,
        sellerTerm: NdaTerms,
        isBuyerInAgreement: boolean
    ) => {
        const nextDvmOptions: Array<{ value: string; label: string }> = [];
        if (
            sellerTerm.nonSolicitationDvmNamesVisible != null &&
            buyerTerm.nonSolicitationDvmNamesVisible != null &&
            (!isBuyerInAgreement ||
                sellerTerm.nonSolicitationDvmNamesVisible <=
                    buyerTerm.nonSolicitationDvmNamesVisible)
        ) {
            nextDvmOptions.push({
                value: JSON.stringify({
                    visibilityType: 'Visible',
                    duration: sellerTerm.nonSolicitationDvmNamesVisible,
                }),
                label: `Visible ${formatYears(
                    sellerTerm.nonSolicitationDvmNamesVisible
                )}`,
            });
        }
        if (
            sellerTerm.nonSolicitationDvmNamesBlinded != null &&
            buyerTerm.nonSolicitationDvmNamesBlinded != null &&
            (!isBuyerInAgreement ||
                sellerTerm.nonSolicitationDvmNamesBlinded <=
                    buyerTerm.nonSolicitationDvmNamesBlinded)
        ) {
            nextDvmOptions.push({
                value: JSON.stringify({
                    visibilityType: 'Blinded',
                    duration: sellerTerm.nonSolicitationDvmNamesBlinded,
                }),
                label: `Blinded ${formatYears(
                    sellerTerm.nonSolicitationDvmNamesBlinded
                )}`,
            });
        }
        setDvmOptions(nextDvmOptions);
        if (nextDvmOptions.length === 1) {
            const single = JSON.parse(
                nextDvmOptions[0].value
            ) as NonSolicitationChoice;
            setDvmChoice(single);
        }
    };

    const submit = () => {
        setIsLoading(true);
        const buyerTerm = getBuyerTerm();
        const payload = {
            ...buyerTerm,
            nonDisclosurePeriod: nonDisclosurePeriod,
            nonSolicitationDvmNamesVisible:
                dvmChoice?.visibilityType === 'Visible'
                    ? dvmChoice?.duration
                    : null,
            nonSolicitationDvmNamesBlinded:
                dvmChoice?.visibilityType === 'Blinded'
                    ? dvmChoice?.duration
                    : null,
            nonSolicitationStaffNamesVisible:
                staffChoice?.visibilityType === 'Visible'
                    ? staffChoice?.duration
                    : null,
            nonSolicitationStaffNamesBlinded:
                staffChoice?.visibilityType === 'Blinded'
                    ? staffChoice?.duration
                    : null,
        };
        axios
            .post(
                `/api/Marketplace/${props.engagementId}/MarketplaceEventActions/NdaTerm`,
                payload
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'Something went wrong during saving form',
                        'error'
                    );
                } else {
                    props.fetchData();
                    props.onSubmit();
                    props.setIsVisible(false);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const formatYears = (type: DataNdaTermsType) => {
        if (type === DataNdaTermsType.OneYear) return '1 Year';
        if (type === DataNdaTermsType.NA) return 'N/A';

        const string = type.toString();

        return `${string} Years`;
    };

    const renderRadioGroup = (
        options: Array<{ value: string; label: string }>,
        selected: NonSolicitationChoice | null,
        setChoice: React.Dispatch<
            React.SetStateAction<NonSolicitationChoice | null>
        >
    ) => {
        if (!options.length) return <span> — </span>;
        if (options.length === 1 && selected)
            return <span>{options[0].label}</span>;
        return (
            <div className="input-options">
                {options.map((opt) => (
                    <div
                        className="input-wrapper"
                        onClick={() => {
                            const parsed = JSON.parse(
                                opt.value
                            ) as NonSolicitationChoice;
                            setChoice(parsed);
                        }}>
                        <input
                            type="radio"
                            checked={
                                selected &&
                                JSON.stringify(selected) === opt.value
                            }
                            onChange={() => {}}
                        />
                        {opt.label}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <Rodal
            visible={props.isVisible}
            onClose={() => props.setIsVisible(false)}
            animation="slideUp"
            className="data-final-nda-terms-popup"
            showMask
            width={400}
            showCloseButton>
            <ModalHeader>Data NDA Final Terms</ModalHeader>
            <ModalBody>
                <div className="data-final-nda-terms-popup">
                    <Row>
                        <label className="mr-1">Non-Disclosure Period:</label>
                        {nonDisclosurePeriod != null
                            ? formatYears(nonDisclosurePeriod)
                            : '—'}
                    </Row>
                    <Row>
                        <label className="mr-1">Non-Solicitation Period:</label>
                    </Row>
                    <Row>
                        <div className="non-solicitation-period">
                            <div className="sub-title">
                                <label className="mr-1">
                                    DVMs / Management:
                                </label>
                                {renderRadioGroup(
                                    dvmOptions,
                                    dvmChoice,
                                    setDvmChoice
                                )}
                            </div>
                            <div className="sub-title">
                                <label className="mr-1"> Staff:</label>
                                {renderRadioGroup(
                                    staffOptions,
                                    staffChoice,
                                    setStaffChoice
                                )}
                            </div>
                        </div>
                    </Row>
                    <Row>
                        <div className="buttons-wrapper">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => props.setIsVisible(false)}>
                                Cancel & Close
                            </button>
                            <ButtonLoader
                                type="submit"
                                className={'btn btn-primary'}
                                disabled={
                                    nonDisclosurePeriod == null ||
                                    dvmChoice == null ||
                                    staffChoice == null ||
                                    isLoading
                                }
                                isLoading={isLoading}
                                loaderButtonText={''}
                                buttonText={'Execute NDA'}
                                onClick={() => submit()}
                            />
                        </div>
                    </Row>
                </div>
            </ModalBody>
        </Rodal>
    );
};
